import React, { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Container } from '../layout/Container';
import { HeaderSection } from '../layout/CardsContainer';
import Button from '../components/Button';
import NavTab from '../components/NavTab';
import ENV_CONFIG from '../config/environment-unified';
import useTranslation from '../hooks/useTranslation';

const MyEvents = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const isLoggedIn = useSelector(state => state.user.isLoggedIn);
  const [myEvents, setMyEvents] = useState([]);
  const [joinedEvents, setJoinedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invitations, setInvitations] = useState([]);
  const [activeTab, setActiveTab] = useState(1); // NavTab is 1-based

  const tabs = [t('myevents.my_events'), t('myevents.joined_events'), t('myevents.invitations', 'Invitations')];

  // Allow deep-linking to Invitations tab (used by navbar notification bell)
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const tab = (params.get('tab') || '').toLowerCase();
    if (tab === 'invitations' || tab === 'invitation' || tab === 'invite' || tab === '3') {
      setActiveTab(3);
    } else if (tab === 'joined' || tab === '2') {
      setActiveTab(2);
    } else if (tab === 'my' || tab === '1') {
      setActiveTab(1);
    }
  }, [location.search]);

  // Check authentication and fetch user events
  useEffect(() => {
    const fetchUserEvents = async () => {
      try {
        setLoading(true);
        
        if (!isLoggedIn) {
          toast.error(t('myevents.please_login_to', 'Please login to view your events'));
          history.push('/?noAuth');
          return;
        }

        // Fetch events created by user
        const myEventsResponse = await axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/my-events`, {
          withCredentials: true
        });
        console.log('📅 My events loaded:', myEventsResponse.data);
        setMyEvents(myEventsResponse.data.events || []);

        // Fetch events user has joined
        const joinedEventsResponse = await axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/joined-events`, {
          withCredentials: true
        });
        console.log('🎫 Joined events loaded:', joinedEventsResponse.data);
        setJoinedEvents(joinedEventsResponse.data.events || []);

        // Fetch tournament invitations for this user
        const invitationsResponse = await axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/my-invitations`, {
          withCredentials: true
        });
        console.log('🎟️ Invitations loaded:', invitationsResponse.data);
        setInvitations(invitationsResponse.data.invitations || []);

      } catch (error) {
        console.error('❌ Error fetching user events:', error);
        if (error.response?.status === 401) {
          toast.error(t('myevents.please_login_to', 'Please login to view your events'));
          history.push('/?noAuth');
        } else {
          toast.error(t('myevents.failed_to_load', 'Failed to load your events'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserEvents();
  }, [isLoggedIn, history, t]);

  const publishEvent = async (slug) => {
    try {
      await axios.put(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/${slug}/publish`, {}, {
        withCredentials: true
      });
      
      toast.success(t('myevents.published_success', 'Event published successfully!'));
      
      // Refresh the events list
      const myEventsResponse = await axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/my-events`, {
        withCredentials: true
      });
      setMyEvents(myEventsResponse.data.events || []);
      
    } catch (error) {
      console.error('❌ Error publishing event:', error);
      toast.error(t('myevents.failed_to_publish', 'Failed to publish event'));
    }
  };

  // const formatDate = (dateString) => {
  //   const date = new Date(dateString);
  //   return date.toLocaleDateString('en-US', {
  //     weekday: 'short',
  //     year: 'numeric',
  //     month: 'short',
  //     day: 'numeric',
  //     hour: '2-digit',
  //     minute: '2-digit'
  //   });
  // };

  // const getStatusColor = (status) => {
  //   switch (status) {
  //     case 'published': return '#10B981';
  //     case 'active': return '#F59E0B';
  //     case 'completed': return '#6B7280';
  //     case 'cancelled': return '#EF4444';
  //     case 'draft': return '#9CA3AF';
  //     default: return '#6B7280';
  //   }
  // };

  // const getStatusText = (status) => {
  //   switch (status) {
  //     case 'published': return t('myevents.status_published');
  //     case 'active': return t('myevents.status_live');
  //     case 'completed': return t('myevents.status_ended');
  //     case 'cancelled': return t('myevents.status_cancelled');
  //     case 'draft': return t('myevents.status_draft');
  //     default: return status;
  //   }
  // };

  // const getEventTypeIcon = (event) => {
  //   if (event.category?.icon) return event.category.icon;
  //   return '🎉';
  // };

  if (loading) {
    return (
      <Container style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
        <HeaderSection title={t('myevents.my_events')}>
          <div>{t('myevents.loading')}</div>
        </HeaderSection>
      </Container>
    );
  }

  return (
    <Container style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
      <HeaderSection title={t('myevents.my_events')}>
        <Link to="/events/create">
          <Button variant="primary" size="small">
            {t('myevents.create_event')}
          </Button>
        </Link>
      </HeaderSection>

      {/* Navigation Tabs */}
      <NavTab
        list={tabs}
        index={activeTab}
        setIndex={setActiveTab}
      />

      {/* Tab Content */}
      <TabContent>
        {activeTab === 1 && (
          <MyEventsTab>
            {myEvents.length === 0 ? (
              <EmptyState>
                <EmptyIcon>🎪</EmptyIcon>
                <h3>{t('myevents.no_events_created')}</h3>
                <p>{t('myevents.create_first_event_message')}</p>
                <Link to="/events/create">
                  <Button variant="primary">{t('myevents.create_first_event')}</Button>
                </Link>
              </EmptyState>
            ) : (
              <EventsGrid>
                {myEvents.map((event) => (
                  <MyEventCard key={event.id} event={event} publishEvent={publishEvent} />
                ))}
              </EventsGrid>
            )}
          </MyEventsTab>
        )}

        {activeTab === 2 && (
          <JoinedEventsTab>
            {joinedEvents.length === 0 ? (
              <EmptyState>
                <EmptyIcon>🎫</EmptyIcon>
                <h3>{t('myevents.no_events_joined')}</h3>
                <p>{t('myevents.discover_events_message')}</p>
                <Link to="/events">
                  <Button variant="primary">{t('myevents.browse_events')}</Button>
                </Link>
              </EmptyState>
            ) : (
              <EventsGrid>
                {joinedEvents.map((event) => (
                  <JoinedEventCard key={event.id} event={event} />
                ))}
              </EventsGrid>
            )}
          </JoinedEventsTab>
        )}

        {activeTab === 3 && (
          <JoinedEventsTab>
            {invitations.length === 0 ? (
              <EmptyState>
                <EmptyIcon>🎟️</EmptyIcon>
                <h3>No tournament invitations yet</h3>
                <p>When organizers invite you to play/stream, they will appear here.</p>
              </EmptyState>
            ) : (
              <EventsGrid>
                {invitations.map((inv) => (
                  <InvitationCard key={inv.assignmentId} invitation={inv} />
                ))}
              </EventsGrid>
            )}
          </JoinedEventsTab>
        )}
      </TabContent>
    </Container>
  );
};

// My Event Card Component (for events I created)
const MyEventCard = ({ event, publishEvent }) => {
  const { t } = useTranslation();
  const imageUrl = event?.bannerImage || event?.coverImage || null;
  const priceNum = (() => {
    const n = typeof event?.ticketPrice === 'string' ? parseFloat(event.ticketPrice) : Number(event?.ticketPrice);
    return Number.isFinite(n) ? n : 0;
  })();
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return '#10B981';
      case 'active': return '#F59E0B';
      case 'completed': return '#6B7280';
      case 'cancelled': return '#EF4444';
      case 'draft': return '#9CA3AF';
      default: return '#6B7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'published': return t('myevents.status_published', 'Published');
      case 'active': return t('myevents.status_live', 'Live');
      case 'completed': return t('myevents.status_ended', 'Ended');
      case 'cancelled': return t('myevents.status_cancelled', 'Cancelled');
      case 'draft': return t('myevents.status_draft', 'Draft');
      default: return status;
    }
  };

  const isTournament = event.category?.slug === 'tournament';
  const viewLink = isTournament
    ? `/events/${event.slug}/dashboard`
    : `/stream-dashboard?eventSlug=${event.slug}`;

  return (
    <StyledEventCard>
      <EventCardHeader>
        {imageUrl && (
          <EventImage
            src={imageUrl}
            alt={event.name}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <EventBadge color={event.category?.color || '#6B7280'}>
          {event.category?.icon} {event.category?.name}
        </EventBadge>
        <EventStatus color={getStatusColor(event.status)}>
          {getStatusText(event.status)}
        </EventStatus>
      </EventCardHeader>

      <EventCardBody>
        <EventTitle>{event.name}</EventTitle>
        <EventDescription>
          {event.description?.substring(0, 100)}
          {event.description?.length > 100 && '...'}
        </EventDescription>
        
        <EventMeta>
          <EventDate>📅 {formatDate(event.startDate)}</EventDate>
          <EventParticipants>
            👥 {event.currentParticipants || 0}
            {event.maxParticipants && ` / ${event.maxParticipants}`}
          </EventParticipants>
        </EventMeta>

        <EventPrice paid={priceNum > 0}>
          {priceNum > 0 ? `${priceNum} ${t('common.gcoins', 'G-Coins')}` : t('common.free', 'Free')}
        </EventPrice>

        <EventActions>
          <Link to={viewLink}>
            <Button variant="primary" size="small">{t('common.manage', 'Manage')}</Button>
          </Link>
          {event.status === 'draft' && (
            <Button 
              variant="secondary" 
              size="small"
              onClick={() => publishEvent(event.slug)}
            >
              {t('common.publish', 'Publish')}
            </Button>
          )}
        </EventActions>
      </EventCardBody>
    </StyledEventCard>
  );
};

// Joined Event Card Component (for events I joined)
const JoinedEventCard = ({ event }) => {
  const { t } = useTranslation();
  const imageUrl = event?.bannerImage || event?.coverImage || null;
  const priceNum = (() => {
    const n = typeof event?.ticketPrice === 'string' ? parseFloat(event.ticketPrice) : Number(event?.ticketPrice);
    return Number.isFinite(n) ? n : 0;
  })();
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return '#10B981';
      case 'active': return '#F59E0B';
      case 'completed': return '#6B7280';
      case 'cancelled': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'published': return t('myevents.joined_status_registered', 'Registered');
      case 'active': return t('myevents.joined_status_live_now', 'Live Now');
      case 'completed': return t('myevents.joined_status_attended', 'Attended');
      case 'cancelled': return t('myevents.status_cancelled', 'Cancelled');
      default: return status;
    }
  };

  const getParticipantStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#10B981';
      case 'pending': return '#F59E0B';
      case 'rejected': return '#EF4444';
      default: return '#6B7280';
    }
  };

  return (
    <StyledEventCard>
      <EventCardHeader>
        {imageUrl && (
          <EventImage
            src={imageUrl}
            alt={event.name}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <EventBadge color={event.category?.color || '#6B7280'}>
          {event.category?.icon} {event.category?.name}
        </EventBadge>
        <EventStatus color={getStatusColor(event.status)}>
          {getStatusText(event.status)}
        </EventStatus>
      </EventCardHeader>

      <EventCardBody>
        <EventTitle>{event.name}</EventTitle>
        <EventDescription>
          {event.description?.substring(0, 100)}
          {event.description?.length > 100 && '...'}
        </EventDescription>
        
        <EventMeta>
          <EventDate>📅 {formatDate(event.startDate)}</EventDate>
          <EventOrganizer>👤 {event.organizer?.name || event.organizer?.username}</EventOrganizer>
        </EventMeta>

        <EventPrice paid={priceNum > 0}>
          {priceNum > 0 ? `${priceNum} ${t('common.gcoins', 'G-Coins')}` : t('common.free', 'Free')}
        </EventPrice>

        {event.participantStatus && (
          <ParticipantStatus color={getParticipantStatusColor(event.participantStatus)}>
            {t('eventdashboard.status', 'Status')}: {event.participantStatus.charAt(0).toUpperCase() + event.participantStatus.slice(1)}
          </ParticipantStatus>
        )}

        <EventActions>
          <Link to={`/events/${event.slug}`}>
            <Button variant="primary" size="small">{t('events.details', 'Details')}</Button>
          </Link>
          {event.category?.slug === 'tournament' && event.tournamentId && (
            <Link to={`/events/${event.slug}/watch`}>
              <Button variant="secondary" size="small">{t('events.watch', 'Watch')}</Button>
            </Link>
          )}
        </EventActions>
      </EventCardBody>
    </StyledEventCard>
  );
};

// Invitation Card Component (for tournaments where I'm invited as player/streamer)
const InvitationCard = ({ invitation }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const event = invitation.event;
  if (!event) return null;
  const imageUrl = event?.bannerImage || event?.coverImage || null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'inactive': return t('myevents.invitation_status_pending', 'Pending');
      case 'active': return t('myevents.invitation_status_accepted', 'Accepted');
      case 'removed': return t('myevents.invitation_status_declined', 'Declined');
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'inactive': return '#F59E0B';
      case 'active': return '#10B981';
      case 'removed': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const handleAccept = async () => {
    try {
      await axios.post(
        `${ENV_CONFIG.EVENTS_ENDPOINT}/events/invitations/${invitation.assignmentId}/accept`,
        {},
        { withCredentials: true }
      );
      toast.success(t('myevents.invitation_accepted', 'Invitation accepted'));
      history.go(0);
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast.error(t('myevents.failed_accept_invitation', 'Failed to accept invitation'));
    }
  };

  const handleDecline = async () => {
    try {
      await axios.post(
        `${ENV_CONFIG.EVENTS_ENDPOINT}/events/invitations/${invitation.assignmentId}/decline`,
        {},
        { withCredentials: true }
      );
      toast.success(t('myevents.invitation_declined', 'Invitation declined'));
      history.go(0);
    } catch (error) {
      console.error('Error declining invitation:', error);
      toast.error(t('myevents.failed_decline_invitation', 'Failed to decline invitation'));
    }
  };

  const statusLabel = getStatusLabel(invitation.status);
  const statusColor = getStatusColor(invitation.status);
  const priceNum = (() => {
    const n = typeof event?.ticketPrice === 'string' ? parseFloat(event.ticketPrice) : Number(event?.ticketPrice);
    return Number.isFinite(n) ? n : 0;
  })();

  return (
    <StyledEventCard>
      <EventCardHeader>
        {imageUrl && (
          <EventImage
            src={imageUrl}
            alt={event.name}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <EventBadge color={event.category?.color || '#6B7280'}>
          {event.category?.icon} {event.category?.name}
        </EventBadge>
        <EventStatus color={statusColor}>
          {statusLabel}
        </EventStatus>
      </EventCardHeader>

      <EventCardBody>
        <EventTitle>{event.name}</EventTitle>
        <EventDescription>
          {event.description?.substring(0, 100)}
          {event.description?.length > 100 && '...'}
        </EventDescription>

        <EventMeta>
          <EventDate>📅 {formatDate(event.startDate)}</EventDate>
          <EventOrganizer>🎮 {t('common.role', 'Role')}: {invitation.role || t('myevents.role_player', 'player')}</EventOrganizer>
        </EventMeta>

        <EventPrice paid={priceNum > 0}>
          {priceNum > 0 ? `${priceNum} ${t('common.gcoins', 'G-Coins')}` : t('common.free', 'Free')}
        </EventPrice>

        <EventActions>
          <Link to={`/events/${event.slug}`}>
            <Button variant="secondary" size="small">{t('myevents.view_event', 'View Event')}</Button>
          </Link>
          {invitation.status === 'inactive' && (
            <>
              <Button
                variant="primary"
                size="small"
                onClick={handleAccept}
              >
                {t('common.accept', 'Accept')}
              </Button>
              <Button
                variant="danger"
                size="small"
                onClick={handleDecline}
              >
                {t('common.decline', 'Decline')}
              </Button>
            </>
          )}
        </EventActions>
      </EventCardBody>
    </StyledEventCard>
  );
};

// Styled Components
const TabContent = styled.div`
  margin-top: 2rem;
`;

const MyEventsTab = styled.div``;
const JoinedEventsTab = styled.div``;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: ${props => props.theme.cardBg};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.border};
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
`;

const StyledEventCard = styled.div`
  background: ${props => props.theme.cardBg};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.border};
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.theme.primary};
  }
`;

const EventCardHeader = styled.div`
  position: relative;
  height: 180px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EventImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
`;

const EventBadge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  background: ${props => props.color};
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  z-index: 2;
`;

const EventStatus = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: ${props => props.color};
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  z-index: 2;
`;

const EventCardBody = styled.div`
  padding: 1.5rem;
`;

const EventTitle = styled.h3`
  color: ${props => props.theme.text};
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const EventDescription = styled.p`
  color: ${props => props.theme.textSecondary};
  margin: 0 0 1rem 0;
  line-height: 1.5;
`;

const EventMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const EventDate = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
`;

const EventParticipants = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
`;

const EventOrganizer = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
`;

const ParticipantStatus = styled.div`
  background: ${props => props.color};
  color: white;
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 1rem;
  display: inline-block;
`;

const EventActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const EventPrice = styled.div`
  margin-top: 0.75rem;
  display: inline-flex;
  align-items: center;
  font-weight: 800;
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 999px;
  width: fit-content;
  color: #111827;
  background: ${({ paid }) => (paid ? '#FBBF24' : '#E5E7EB')};
`;

export default MyEvents;
